import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, FormControl, Grid } from '@mui/material'
import AuthService from '../../services/login'
import { Link, useNavigate } from 'react-router-dom'
import { FormSuccess } from './form-success'
import { FormError } from './form-error'
import { loginValidators } from '../validators/login'
import { useTranslation } from 'react-i18next'
import MyButton from '../components/buttons'
import MyInput from './MyInput'
import {
  FaEnvelope,
  FaHome,
  FaLock,
  FaUserAlt,
  FaUserPlus,
} from 'react-icons/fa'
import ForgotPasswordModal from './ForgotPasswordModal'
import CookieManager from '../lib/CookiesManager'

const LoginForm = () => {
  const { t } = useTranslation()
  const [showPassword] = useState(false)
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string>('')
  const [showFormSuccess, setShowFormSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(loginValidators()),
  })

  useEffect(() => {
    const token = CookieManager.getToken()
    if (token) {
      setIsLoggedIn(true)
    }
  }, [])

  const onSubmit = async (data: any) => {
    try {
      const response = await AuthService.login({
        email: data.email,
        password: data.password,
        rememberMe,
      })

      if (response.error) {
        setErrorMessage(response.error)
      } else if (response.success) {
        setSuccessMessage(response.success)
        setShowFormSuccess(true)
        setTimeout(() => {
          navigate('/')
        }, 2000)

        await AuthService.refreshToken()
      }
    } catch (error) {
      if (error instanceof Error && 'response' in error) {
        console.error('Login error:', error.response)
      } else {
        console.error('Login error:', error)
      }

      if ((error as any).response?.status === 401) {
        if ((error as any).response?.data?.error) {
          setErrorMessage((error as any).response.data.error) // ✅ Affiche "Votre adresse email n'est pas confirmée."
        } else {
          setErrorMessage(t('unexpected-error'))
        }
      } else {
        setErrorMessage(t('unexpected-error'))
      }
    }
  }

  const handleLogout = () => {
    AuthService.logout()
    setSuccessMessage(t('logout-success'))
    setShowFormSuccess(true)
    setTimeout(() => {
      setShowFormSuccess(false)
      setIsLoggedIn(false)
      navigate('/')
    }, 3000)
  }

  return (
    <div className='w-full h-screen flex flex-col lg:flex-row overflow-hidden relative'>
      <div
        className='lg:w-1/2 w-full bg-cover bg-left'
        style={{
          backgroundImage: 'url(images/signinSignupBg/sexagogo-bg-login.jpg)',
          backgroundPosition: '15% center',
        }}
      ></div>

      <div className='lg:w-1/2 w-full flex flex-col justify-center items-center p-3 lg:p-12 h-screen'>
        <div className='text-center text-anthracite/70 absolute bottom-1 w-full text-xs'>
          {t('free-announce-desc')}
        </div>
        <div className='w-full max-w-md'>
          {isLoggedIn ? (
            <div className='text-center'>
              <h3 className='text-2xl text-redpink font-semibold mb-3'>
                {t('disconnect-confirm')}
              </h3>
              <div className='bg-gray-100 p-5 rounded-lg flex justify-center gap-4'>
                <MyButton variant='redpink-anthracite' onClick={handleLogout}>
                  {t('disconnect')}
                </MyButton>
                <MyButton variant='anthracite' onClick={() => navigate('/')}>
                  {t('dashboard.to-the-site')}
                </MyButton>
              </div>
              {showFormSuccess && (
                <div className='mt-3'>
                  <FormSuccess message={successMessage} />
                </div>
              )}
            </div>
          ) : (
            <>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className='space-y-6 text-center'
              >
                <div className='absolute right-4 top-4 bg-redpink text-white rounded-lg'>
                  <Link
                    to='/'
                    className='p-2 sm:p-3 flex items-center hover:bg-anthracite transition-all bg-redpink text-white rounded-lg'
                  >
                    <FaHome className='text-xl sm:text-2xl' />
                  </Link>
                </div>
                <div className='w-full'>
                  <h3 className='text-3xl text-redpink font-medium'>
                    {t('login')}
                  </h3>
                  <h2 className='text-sm text-anthracite mb-6 leading-relaxed'>
                    {t('register-account-desc1')}
                  </h2>

                  <FormControl fullWidth className='mb-4'>
                    <MyInput
                      value={watch('email') || ''}
                      type='email'
                      label={t('email')}
                      className='mb-2'
                      {...register('email', {
                        required: t('email-required'),
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: t('invalid-email'),
                        },
                      })}
                      error={errors.email?.message}
                      onChange={(value: string) => setValue('email', value)}
                      min={0}
                      max={0}
                      icon={<FaEnvelope />}
                      autoComplete='email'
                    />
                    <MyInput
                      value={watch('password') || ''}
                      label={t('password')}
                      {...register('password', {
                        required: t('password-required'),
                      })}
                      error={errors.password?.message}
                      onChange={(value: string) => setValue('password', value)}
                      type={showPassword ? 'text' : 'password'}
                      min={0}
                      max={0}
                      className='mt-3'
                      icon={<FaLock />}
                      autoComplete='current-password'
                      showClearButton={false}
                    />
                  </FormControl>

                  <div className='mt-2'>
                    {errorMessage && <FormError message={errorMessage} />}
                  </div>
                  <div className=''>
                    <button
                      type='button'
                      className='text-sm text-anthracite underline'
                      onClick={() => setIsForgotPasswordOpen(true)}
                    >
                      {t('password-forgotten')}
                    </button>
                  </div>
                  <MyButton
                    variant='green'
                    icon={<FaEnvelope />}
                    className='w-full py-3 mt-2 text-white font-semibold'
                    onClick={handleSubmit(onSubmit)}
                  >
                    {t('login')}
                  </MyButton>
                </div>
                <div className='w-full bg-redpink text-white p-5 rounded-lg'>
                  <h3 className='text-white'>{t('create-account')}</h3>
                  <h2 className='text-sm text-white mb-4 leading-relaxed'>
                    {t('register-account-desc2')}
                  </h2>
                  <MyButton
                    variant='anthracite'
                    width='full'
                    icon={<FaUserPlus />}
                    onClick={() => navigate('/register')}
                    className='w-full py-3 '
                  >
                    {t('create-account')}
                  </MyButton>
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      <ForgotPasswordModal
        isOpen={isForgotPasswordOpen}
        onClose={() => setIsForgotPasswordOpen(false)}
        emailLabel={t('actual-email')}
      />
    </div>
  )
}

export default LoginForm
